import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {brandStatus, organizationTypes} from '@packages/domains/aeo/data-types';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'update-brand-context' as const;

const addressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

const argumentZod = z.object({
  brandId: z.string().describe('The brand ID to update.'),
  name: z.string().optional().describe('Updated brand name.'),
  description: z.string().optional().describe('Updated brand description.'),
  aliases: z
    .array(z.string())
    .optional()
    .describe('Updated alternative names.'),
  status: z.enum(brandStatus).optional().describe('Updated status.'),
  contact: z
    .object({
      email: z.string().optional(),
      telephone: z.string().optional(),
    })
    .optional()
    .describe('Updated contact information.'),
  about: z
    .object({
      foundingDate: z.string().optional().describe('ISO date string.'),
      organizationType: z.enum(organizationTypes).optional(),
      industry: z.string().optional(),
      description: z.string().optional(),
    })
    .optional()
    .describe('Updated about information.'),
  locations: z
    .array(
      z.object({
        name: z.string(),
        isHeadquarters: z.boolean().optional(),
        address: addressSchema,
      })
    )
    .optional()
    .describe('Updated office locations (replaces entire array).'),
  people: z
    .array(
      z.object({
        name: z.string(),
        role: z.string().optional(),
      })
    )
    .optional()
    .describe('Updated key people (replaces entire array).'),
  competitors: z
    .array(
      z.object({
        name: z.string(),
        aliases: z.array(z.string()).optional(),
        domains: z.array(z.string()).optional(),
      })
    )
    .optional()
    .describe('Updated competitors (replaces entire array).'),
});

export const updateBrandContextManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Update brand context',
  family: 'brand',
  llmTool: {
    name: toolName,
    description:
      'Updates an existing brand for the current site (from MCP session context). All fields are optional — only provided fields will be updated. Array fields (locations, people, competitors) replace the entire array.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.AEO_MEASUREMENT,
  confirmBeforeExecuting: true,
});

export type UpdateBrandContextArgumentType = z.infer<typeof argumentZod>;
